import React, { useState, useEffect } from "react";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import RefreshButton from "../../../Component/Common/RefreshButton";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import EmployeeForm from "./EmployeeForm";
import { Badge } from "../../../Component/AdminComponents/Badge";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import axios from "axios";
import { BASE_URL } from "../../../config/api";
import { useNavigate } from "react-router-dom";
import SearchInput from "../../../Component/Common/SearchInput";
import AddButton from "../../../Component/Common/AddButton";
import SimpleTable from "../../../Component/Common/SimpleTable";
import UpdateButton from "../../../Component/Common/UpdateButton";
import StatusButton from "../../../Component/Common/StatusButton";
import Pagination from "../../../Component/Common/Pagination";
import { Eye } from "lucide-react";

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [managers, setManagers] = useState([]);
  console.log("manager list: ",managers)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const sessionUser = localStorage.getItem("authUserData");
  const companyId = sessionUser ? JSON.parse(sessionUser)?.id : null;
  const navigate = useNavigate();

  /* ---------------- DEBOUNCE SEARCH ---------------- */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // reset page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  /* ---------------- FETCH EMPLOYEES (WITH SEARCH) ---------------- */
  const fetchEmployees = async (page = 1, searchValue = "") => {
    if (!companyId) {
      toast.error("Company ID not found!");
      return;
    }

    const searchParam = searchValue
      ? `search_value=${encodeURIComponent(searchValue)}`
      : "";

    try {
      setLoading(true);

      const res = await axios.post(
        `${BASE_URL}company/employee/all?${searchParam}`,
        { company_id: companyId },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data?.status) {
        const apiData = res.data.data || {};
        const employeesList = apiData.data || [];

        setEmployees(employeesList);

        // managers must ALWAYS be array
        setManagers(
          employeesList.filter(
            (e) => e.position?.toUpperCase() === "MANAGER"
          )
        );

        setCurrentPage(apiData.current_page || 1);
        setTotalPages(apiData.last_page || 1);
      } else {
        toast.error(res.data?.message || "Failed to fetch employees");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- INITIAL + PAGINATION FETCH ---------------- */
  useEffect(() => {
    fetchEmployees(currentPage, debouncedSearch);
  }, [companyId, currentPage, debouncedSearch]);

  /* ---------------- TOGGLE STATUS ---------------- */
  const toggleStatus = async (emp) => {
    const newStatus = emp.status === 1 ? 0 : 1;

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Change status to ${newStatus === 1 ? "Active" : "Inactive"}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.post(
        `${BASE_URL}company/employee/status/${emp.id}`,
        { status: newStatus }
      );

      if (res.data?.status) {
        toast.success("Status updated");
        fetchEmployees(currentPage, debouncedSearch);
      } else {
        toast.error("Failed to update status");
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  /* ---------------- MODAL HANDLERS ---------------- */
  const openModal = (emp = null) => {
    setSelectedEmployee(emp);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedEmployee(null);
    setModalOpen(false);
  };

  const employeeRows = employees.map((emp) => ({
  raw: emp, // 🔥 full object for actions
  cells: [
    emp.employee_id,
    {
      title: `${emp.first_name} ${emp.last_name}`,
      sub: emp.position,
    },
    emp.official_phone || "—",
    emp.official_email || "—",
    // emp.position || "—",
    emp.type_of_user || "—",
    {
      title: (
        <div className="flex flex-wrap gap-1 justify-center">
          {emp.categories?.map((cat, i) => (
            <Badge key={i} color="blue">
              {cat}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      title: (
        <div className="flex flex-wrap gap-1 justify-center">
          {emp.handle?.map((h, i) => (
            <Badge key={i} color="green">
              {h}
            </Badge>
          ))}
        </div>
      ),
    },
    // emp.pincodes?.join(", ") || "—",
    // {
    //   title: (
    //     <StatusButton onClick={(e) => {
    //         e.stopPropagation();
    //         toggleStatus(emp);
    //       }} label={emp.status === 1 ? "Active" : "Inactive"} />
        
    //   ),
    // },
  ],
}));

  return (
    <div className="space-y-4">
      <SectionCard title="Employee Directory & Details">
        {/* Search + Buttons */}
        <div className="flex justify-end mb-3 gap-2">
          <SearchInput
            value={search}
            placeholder="Search employee..."
            onChange={setSearch}
            onClear={() => setSearch("")}
          />
          <AddButton onClick={() => navigate("/employees/add")}/>
          <div className="hidden lg:block">
            <RefreshButton onClick={fetchEmployees} />
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center h-[50vh]">
            <RegularLoader />
          </div>
        )}

        {!loading && employees.length === 0 && (
          <p className="text-center text-sm text-slate-500 py-3">
            No matching employees found.
          </p>
        )}

        {!loading && employees.length > 0 && (
          <>

          {/* ===== MOBILE CARD VIEW ===== */}
        <div className="md:hidden space-y-4">
          {employees.map((emp) => (
            <div
              key={emp.id}
              className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm"
            >

              {/* ===== TOP SECTION ===== */}
              <div className="flex justify-between items-start">

                <div className="flex gap-3">

                  {/* Profile Image */}
                  <div className="h-12 w-12 rounded-xl overflow-hidden bg-slate-200">
                    {emp.photo_url ? (
                      <img
                        src={emp.photo_url}
                        alt="profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center font-semibold text-slate-600">
                        {emp.first_name?.charAt(0)}
                      </div>
                    )}
                  </div>

                  {/* Name + Email */}
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {emp.first_name} {emp.last_name}
                    </div>

                    <div className="text-xs text-slate-500">
                      {emp.official_email || "—"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {emp.position || "—"}
                    </div>
                  </div>
                </div>

                {/* Rating (Only if exists in API) */}
                {emp.rating && (
                  <div className="flex items-center gap-1 text-sm font-medium text-slate-700">
                    ⭐ {emp.rating}
                  </div>
                )}
              </div>

              {/* ===== LOCATION ===== */}
              <div className="mt-3 text-xs text-slate-500">
                <div className="flex items-start gap-2">
                  📍
                  <div>
                    <span className="font-medium text-slate-600">Area Handled:</span>

                    <div className="mt-1 flex flex-wrap gap-1">

                      {/* Convert state string to array */}
                      {(emp.state ? emp.state.split(",") : [])
                        .slice(
                          0,
                          expandedEmployees[emp.id]
                            ? emp.state.split(",").length
                            : 2
                        )
                        .map((state, i) => (
                          <span
                            key={`state-${i}`}
                            className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-[11px]"
                          >
                            {state.trim()}
                          </span>
                        ))}

                      {/* Convert district string to array */}
                      {(emp.district ? emp.district.split(",") : [])
                        .slice(
                          0,
                          expandedEmployees[emp.id]
                            ? emp.district.split(",").length
                            : 2
                        )
                        .map((district, i) => (
                          <span
                            key={`district-${i}`}
                            className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-[11px]"
                          >
                            {district.trim()}
                          </span>
                        ))}

                    </div>

                    {/* VIEW MORE BUTTON */}
                    {(
                      (emp.state && emp.state.split(",").length > 2) ||
                      (emp.district && emp.district.split(",").length > 2)
                    ) && (
                      <button
                        onClick={() =>
                          setExpandedEmployees((prev) => ({
                            ...prev,
                            [emp.id]: !prev[emp.id],
                          }))
                        }
                        className="mt-1 text-blue-600 text-[11px] font-medium hover:underline"
                      >
                        {expandedEmployees[emp.id] ? "View Less" : "View More"}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* ===== SKILLS + STATS ===== */}
              <div className="flex justify-between items-start mt-4">

                {/* Skills */}
                <div>
                  <div className="text-xs text-slate-500 mb-2">Categories</div>

                  <div className="flex flex-wrap gap-2">
                    {emp.categories?.map((cat, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 text-[11px] bg-blue-100 text-blue-700 rounded-lg border border-slate-200"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                {/* <div className="text-xs text-slate-600 space-y-2 text-right">
                  <div>
                    <span className="text-slate-400">Tasks:</span>{" "}
                    <span className="font-semibold">
                      {emp.total_tasks || 0}
                    </span>
                  </div>

                  <div>
                    <span className="text-slate-400">Hours:</span>{" "}
                    <span className="font-semibold">
                      {emp.total_hours || 0}
                    </span>
                  </div>
                </div> */}

              </div>

              {/* ===== BOTTOM SECTION ===== */}
              <div className="flex justify-between items-center mt-5 pt-3 border-t border-slate-100">

                <div className="text-xs text-slate-400">
                  Applied: {emp.created_at ? new Date(emp.created_at).toLocaleDateString() : "—"}
                </div>

                <div className="flex gap-2">

                  {/* Status Button */}
                  {/* <button
                    className={`px-4 py-1.5 text-xs rounded-xl border ${
                      emp.status === 1
                        ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                        : "bg-slate-100 text-slate-600 border-slate-200"
                    }`}
                  >
                    {emp.status === 1 ? "Online" : "Offline"}
                  </button> */}

                  {/* View Button */}
                  <button
                    onClick={() =>
                      navigate(`/employees/${emp.id}`, {
                        state: { employee: emp },
                      })
                    }
                    className="flex gap-2 px-4 py-1.5 text-xs rounded-xl bg-blue-800 text-white hover:bg-black transition"
                  >
                    <Eye size={14} />
                    View Profile
                  </button>

                </div>
              </div>

            </div>
          ))}
        </div>
        
          <div className="hidden md:block">
            <SimpleTable
                headers={[
                  "Emp ID",
                  "Name",
                  "Phone",
                  "Email",
                  // "Position",
                  "Type",
                  "Categories",
                  "Handle",
                  // "Pincodes",
                  // "Status",
                ]}
                rows={employeeRows}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                renderActions={(emp) => (
                  <div className="flex justify-center gap-1">

                    <button
                      onClick={() =>
                        navigate(`/employees/${emp.id}`, {
                          state: { employee: emp }, // ✅ pass full row object
                        })
                      }
                      className="text-xs px-3 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    >
                      View
                    </button>

                    <UpdateButton onClick={() => openModal(emp)} />

                  </div>
                )}
              />
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center pt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
          </>
        )}
      </SectionCard>
      {/* ---------------- Custom Modal ---------------- */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-start z-50 overflow-y-auto p-4">
          <div className="bg-white w-full max-w-6xl rounded-xl p-10 mt-10 shadow-lg relative">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              onClick={closeModal}
            >
              ✕
            </button>

            <EmployeeForm
              managers={managers} 
              employee={selectedEmployee}
              onSubmit={() => {
                fetchEmployees();
                closeModal();
              }}
              onClose={closeModal}
            />
          </div>
        </div>
      )}
    </div>
  );
}


// import { Plus, Search } from "lucide-react";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import Swal from "sweetalert2";
// import toast from "react-hot-toast";
// import { Badge } from "../../../Component/AdminComponents/Badge";

// export default function Employees() {
//   const navigate = useNavigate();

//   const [employees, setEmployees] = useState([]);
//   const [filteredEmployees, setFilteredEmployees] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");

//   // Get company_id from session
//   const sessionUser = localStorage.getItem("authUserData");
//   const companyId = sessionUser ? JSON.parse(sessionUser)?.id : null;

//   // -------------------------------
//   // 🔍 Debounce Search
//   // -------------------------------
//   useEffect(() => {
//     const timer = setTimeout(() => setDebouncedSearch(search), 300);
//     return () => clearTimeout(timer);
//   }, [search]);

//   // -------------------------------
//   // 📌 Fetch Employee List (POST)
//   // -------------------------------
//   const fetchEmployees = async () => {
//     if (!companyId) {
//       toast.error("Company ID not found!");
//       return;
//     }

//     try {
//       setLoading(true);

//       const response = await axios.post(
//         "${BASE_URL}/company/employee/all",
//         { company_id: companyId },
//         {
//           headers: {
//             "Content-Type": "application/json",
//           },
//         }
//       );

//       if (response.data?.status) {
//         setEmployees(response.data.data || []);
//         setFilteredEmployees(response.data.data || []);
//       } else {
//         toast.error("Failed to fetch employees!");
//       }
//     } catch (error) {
//       console.error("Error fetching employees:", error);
//       toast.error("Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchEmployees();
//   }, [companyId]);

//   // -------------------------------
//   // 🔎 Server-side Search
//   // -------------------------------
//   useEffect(() => {
//     if (!debouncedSearch) {
//       fetchEmployees();
//       return;
//     }

//     const searchEmployees = async () => {
//       try {
//         setLoading(true);

//         const response = await axios.post(
//           "${BASE_URL}/company/employee/search",
//           {
//             company_id: companyId,
//             search: debouncedSearch,
//           },
//           {
//             headers: { "Content-Type": "application/json" },
//           }
//         );

//         if (response.data?.status) {
//           setFilteredEmployees(response.data.data || []);
//         } else {
//           toast.error(response.data?.message || "Failed to fetch search results");
//           setFilteredEmployees([]);
//         }
//       } catch (err) {
//         console.error("Error searching employees:", err);
//         toast.error("Something went wrong");
//         setFilteredEmployees([]);
//       } finally {
//         setLoading(false);
//       }
//     };

//     searchEmployees();
//   }, [debouncedSearch, companyId]);

//   // -------------------------------
//   // 🔥 Toggle Active / Inactive
//   // -------------------------------
//   const toggleStatus = async (emp) => {
//     const newStatus = emp.status === 1 ? 0 : 1;

//     const confirm = await Swal.fire({
//       title: "Are you sure?",
//       text: `Change status to ${newStatus === 1 ? "Active" : "Inactive"}?`,
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonColor: "#2563eb",
//       cancelButtonColor: "#d33",
//       confirmButtonText: "Yes",
//     });

//     if (!confirm.isConfirmed) return;

//     try {
//       const res = await axios.post(
//         `${BASE_URL}/company/employee/status/${emp.id}`,
//         { status: newStatus }
//       );

//       if (res.data?.status) {
//         toast.success("Status updated");
//         fetchEmployees();
//       } else {
//         toast.error("Failed to update status");
//       }
//     } catch (err) {
//       toast.error("Something went wrong");
//     }
//   };

//   return (
//     <div className="space-y-4">
//       <SectionCard title="Employee Directory & Details">
//         {/* ---------------------- */}
//         {/* Search + Buttons */}
//         {/* ---------------------- */}
//         <div className="flex justify-end gap-2 items-center mb-2">
//           {/* Search Input */}
//           <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
//             <Search className="w-4 h-4 text-slate-800" />
//             <input
//               type="text"
//               placeholder="Search employee..."
//               className="bg-transparent text-xs outline-none placeholder:text-slate-700"
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//             />
//           </div>

//           {/* Add Employee */}
//           <BlueButton
//             onClick={() => navigate("/add-employee")}
//             className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//           >
//             <Plus size={14} className="mr-1" /> Add
//           </BlueButton>

//           {/* Refresh */}
//           <RefreshButton onClick={fetchEmployees} />
//         </div>

//         {/* ---------------------- */}
//         {/* Loader */}
//         {/* ---------------------- */}
//         {loading && (
//           <div className="flex justify-center items-center h-[50vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* ---------------------- */}
//         {/* No Results */}
//         {/* ---------------------- */}
//         {!loading && filteredEmployees.length === 0 && (
//           <p className="text-center text-sm text-slate-500 py-3">
//             No matching employees found.
//           </p>
//         )}

//         {/* ---------------------- */}
//         {/* Table */}
//         {/* ---------------------- */}
//         {!loading && filteredEmployees.length > 0 && (
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-[10px] sm:text-sm text-center">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500">
//                   <th className="py-2 px-2">Emp ID</th>
//                   <th className="py-2 px-2">Name</th>
//                   <th className="py-2 px-2">Phone</th>
//                   <th className="py-2 px-2">Email</th>
//                   <th className="py-2 px-2">Position</th>
//                   <th className="py-2 px-2">Type</th>
//                   <th className="py-2 px-2">Categories</th>
//                   <th className="py-2 px-2">Handle</th>
//                   <th className="py-2 px-2">Pincodes</th>
//                   <th className="py-2 px-2">Status</th>
//                   <th className="py-2 px-2">Actions</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {filteredEmployees.map((emp) => (
//                   <tr key={emp.id} className="border-b border-slate-50 hover:bg-blue-50/50">
//                     <td className="py-2 px-2">{emp.employee_id}</td>
//                     <td className="py-2 px-2 font-semibold">
//                       {emp.first_name} {emp.last_name}
//                     </td>
//                     <td className="py-2 px-2">{emp.official_phone}</td>
//                     <td className="py-2 px-2">{emp.official_email}</td>
//                     <td className="py-2 px-2">{emp.position}</td>
//                     <td className="py-2 px-2">{emp.type_of_user}</td>
//                     <td className="py-2 px-2">
//                       <div className="flex flex-wrap gap-1 justify-center">
//                         {emp.categories?.map((cat, idx) => (
//                           <Badge key={idx} color="blue">{cat}</Badge>
//                         ))}
//                       </div>
//                     </td>
//                     <td className="py-2 px-2">
//                       <div className="flex flex-wrap gap-1 justify-center">
//                         {emp.handle?.map((h, idx) => (
//                           <Badge key={idx} color="green">{h}</Badge>
//                         ))}
//                       </div>
//                     </td>
//                     <td className="py-2 px-2">{emp.pincodes?.join(", ")}</td>
//                     <td className="py-2 px-2">
//                       <button
//                         onClick={() => toggleStatus(emp)}
//                         className={`px-2 py-1 rounded text-[9px] ${
//                           emp.status === 1
//                             ? "bg-green-50 text-green-700 border border-green-200"
//                             : "bg-red-50 text-red-700 border border-red-200"
//                         }`}
//                       >
//                         {emp.status === 1 ? "Active" : "Inactive"}
//                       </button>
//                     </td>
//                     <td className="py-2 px-2 flex justify-center gap-1">
//                       <button className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-blue-50 text-blue-700 hover:bg-blue-100">
//                         View
//                       </button>
//                       <button className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-slate-50 text-slate-700 hover:bg-slate-100">
//                         Download
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </SectionCard>
//     </div>
//   );
// }





// import React, { useState, useMemo, useEffect } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import { Eye, EyeOff, Plus, RefreshCcw } from "lucide-react";

// // === Import your custom components ===
// import Header from "../../../Component/Common/Header";
// import BlueButton from "../../../Component/Common/BlueButton";

// import Card from "../../../Component/Common/Card";
// import Badge from "../../../Component/Common/Badge";
// import { useNavigate } from "react-router-dom";
// import WarrantyLoader from "../../../Component/Loader/RegularLoader";
// import CreatingAcLoader from "../../../Component/Loader/CreatingAcLoader";
// import SellLoader from "../../../Component/Loader/SellLoader";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { SectionCard } from "../../../Component/AgentComponents/SectionCard";
// import SimpleTable from "../../../Component/Common/SimpleTable";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// // import employeeForm from "../component/employeeForm";

// export default function Employees() {

//   const navigate = useNavigate();
//   const [allemployee, setAllemployee] = useState([]); // for search
//   const [list, setList] = useState([]); // current page
//   const [page, setPage] = useState(1);
//   const [lastPage, setLastPage] = useState(1);
//   const [filter, setFilter] = useState("");
    
//   const [showPwd, setShowPwd] = useState(false);
//   const [showAdd, setShowAdd] = useState(false);
  
//   const [loading, setLoading] = useState(false);

//   // active states map { ID: true/false }
//   const [activeStates, setActiveStates] = useState({});

//   // update password
//   const [showPwdModal, setShowPwdModal] = useState(false);
//   const [selectedemployee, setSelectedemployee] = useState(null);

//   // assign Key
//   const [showAssignModal, setShowAssignModal] = useState(false);
//   const [assignemployee, setAssignemployee] = useState(null);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);
//   const loginUserID = authUser?.id;
//   console.log("senior id:", loginUserID);

//   // === Fetch employee with pagination ===
  
//     const fetchemployee = async () => {
//       if (!loginUserID) return;
//       setLoading(true);
//       try {
//         const res = await axios.get(
//           `${BASE_URL}/company/employee/all`
//         );

//         const fetchedCustomers = res.data.data.data || [];
//         setList(fetchedCustomers);
//         setLastPage(res.data.data.last_page || 1);

//         // ✅ Initialize active states from fetched data
//         const initialStates = {};
//         fetchedCustomers.forEach((customer, index) => {
//           if (
//             customer &&
//             customer.ID !== undefined &&
//             customer.hasOwnProperty("IS_Active")
//           ) {
//             initialStates[customer.ID] = customer.IS_Active === 1;
//           } else {
//             console.warn(`Customer at index ${index} is invalid:`, customer);
//           }
//         });
//         setActiveStates(initialStates);
//       } catch (err) {
//         console.error("Error fetching employee:", err);
//       } finally {
//         setLoading(false);
//       }
//     };
//     useEffect(() => {
//       fetchemployee();
//     }, [loginUserID, page]);

//   useEffect(() => {
//   const fetchAll = async () => {
//     if (!loginUserID) return;
//     try {
//       let all = [];
//       let pageNo = 1;
//       let lastPage = 1;

//       // 🔄 Loop through all pages
//       while (pageNo <= lastPage) {
//         const res = await axios.get(
//           `https://goelectronix.in/api/v1/get-allemployee2?page=${pageNo}&per_page=10&distributortID=${loginUserID}`
//         );

//         const data = res?.data?.data;
//         if (!data) break;

//         all = [...all, ...(data.data || [])];
//         lastPage = data.last_page || 1;
//         pageNo++;
//       }

//       setAllemployee(all);

//       // ✅ Build activeStates once
//       const initialStates = {};
//       all.forEach((customer, index) => {
//         if (customer?.ID !== undefined && "IS_Active" in customer) {
//           initialStates[customer.ID] = customer.IS_Active === 1;
//         } else {
//           console.warn(`Customer at index ${index} is invalid:`, customer);
//         }
//       });
//       setActiveStates(initialStates);
//     } catch (err) {
//       console.error("Error fetching all employee:", err);
//     }
//   };

//   fetchAll();
// }, [loginUserID]);


//   // === Filtered search (client-side) ===
//   const filtered = useMemo(() => {
//   if (!filter) return list; // if no search → show paginated data
//   const term = filter.toLowerCase();
//   return allemployee.filter(
//     (r) =>
//       (r.Shop_Name && r.Shop_Name.toLowerCase().includes(term)) ||
//       (r.employeeCode && r.employeeCode.toLowerCase().includes(term))
//   );
// }, [filter, list, allemployee]);



//   // === Confirm & toggle active/inactive ===
//   // === Confirm & toggle active/inactive ===
//   const handleToggleActiveConfirmation = async (customer) => {
//   const { ID, Shop_Name } = customer;
//   const newActiveState = !activeStates[ID]; // toggled state
//   const action = newActiveState ? "activate" : "deactivate"; // fixed

//   Swal.fire({
//     title: "Are you sure?",
//     text: `Do you want to ${action} ${Shop_Name}?`,
//     icon: "warning",
//     showCancelButton: true,
//     confirmButtonText: `Yes, ${action} it!`,
//     cancelButtonText: "Cancel",
//     buttonsStyling: false,
//     customClass: {
//       popup: "rounded-2xl shadow-lg p-6 bg-white",
//       title: "text-lg font-semibold text-gray-900",
//       content: "text-sm text-gray-600",
//       confirmButton:
//         "bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg",
//       cancelButton:
//         "bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-4 py-2 rounded-lg",
//     },
//   }).then((result) => {
//     if (result.isConfirmed) {
//       handleToggleActive(customer, newActiveState);
//     }
//   });
// };


//   const handleToggleActive = async (customer, newActiveState) => {
//     const { ID, Shop_Name } = customer;

//     // Optimistic update
//     setActiveStates((prev) => ({
//       ...prev,
//       [ID]: newActiveState,
//     }));

//     try {
//       const apiUrl = `https://goelectronix.in/api/v1/update-employee/${ID}`;

//       const response = await fetch(apiUrl, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//           IS_Active: newActiveState ? 1 : 0, // send numeric value
//         }),
//       });

//       if (!response.ok) {
//         // revert if failed
//         setActiveStates((prev) => ({
//           ...prev,
//           [ID]: !newActiveState,
//         }));

//         const errorData = await response.json();
//         Swal.fire({
//           title: "Error",
//           text: `Failed to update status: ${errorData?.message || response.statusText}`,
//           icon: "error",
//           buttonsStyling: false,
//           customClass: {
//             popup: "rounded-2xl shadow-lg p-6 bg-white",
//             title: "text-lg font-semibold text-gray-900",
//             content: "text-sm text-gray-600",
//             confirmButton: "bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg",
//           },
//         });
//         return;
//       }

//       // ✅ Success alert
//       Swal.fire({
//         title: newActiveState ? "Activated" : "Deactivated",
//         text: `employee ${Shop_Name} status changed to ${newActiveState ? "active" : "inactive"}.`,
//         icon: "success",
//         buttonsStyling: false,
//         customClass: {
//           popup: "rounded-2xl shadow-lg p-6 bg-white",
//           title: "text-lg font-semibold text-gray-900",
//           content: "text-sm text-gray-600",
//           confirmButton: "bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg",
//         },
//       });
//     } catch (error) {
//       // revert on error
//       setActiveStates((prev) => ({
//         ...prev,
//         [ID]: !newActiveState,
//       }));

//       Swal.fire({
//         title: "Error",
//         text: `Failed to update status: ${error.message}`,
//         icon: "error",
//         buttonsStyling: false,
//         customClass: {
//           popup: "rounded-2xl shadow-lg p-6 bg-white",
//           title: "text-lg font-semibold text-gray-900",
//           content: "text-sm text-gray-600",
//           confirmButton: "bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg",
//         },
//       });
//     }
//   };


//   return (
//    <div>
//     <SectionCard>
//      <div className="space-y-3 sm:space-y-4">
//       <Header>
//   <div className="flex flex-row sm:flex-row sm:items-center sm:gap-2 gap-2 w-full">
//     {/* Search Input */}
//     <input
//       value={filter}
//       onChange={(e) => setFilter(e.target.value)}
//       placeholder="Search by employee Code or Shop Name"
//       className="w-full sm:w-1/2 rounded-lg border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//     />

//     {/* Buttons */}
//     <div className="flex items-center gap-2 w-full sm:w-auto">
//       <BlueButton
//         onClick={() => navigate("/add-employee")}
//         className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//         style={{ backgroundColor: "#0B5ED7" }}
//         >
//         <Plus className="mr-1" size={14} /> Add
//         </BlueButton>

//       <RefreshButton onClick={fetchemployee} />
//     </div>
//   </div>
// </Header>


//       {loading ? (

//         <div className="flex items-center justify-center w-full translate-y-10">
//             <RegularLoader />
//         </div>
//       ) : (
//         <>
//           <SimpleTable
//             headers={[
//               "Sr No.",
//               "Type of employee",
//               "employee (Code)",
//               "District",
//               "Mobile No",
//               "Senior",
//               "Handle",
//             //   "Key Balance",
//               "Created At",
//               "Actions",
//             ]}
//             rows={filtered.map((r, index) => [
//               <span key={`sr-${r.ID}`}>{index + 1}</span>,

//               <div key={r.ID} className="font-medium">
//                 {r.Shop_Name}{" "}
//                 <span className="text-xs text-slate-500">
//                   ({r.employeeCode})
//                 </span>
//               </div>,

//               <span key={`d-${r.ID}`}>{r.District || "-"}</span>,
//               <span key={`m-${r.ID}`}>{r.MobileNo || "-"}</span>,
//               <span key={`e-${r.ID}`}>
//                 {r.EnterpriseStatus === 1 ? "Created" : "Not Created"}
//               </span>,

//               <Badge
//                 key={`b-${r.ID}`}
//                 label={activeStates[r.ID] ? "Active" : "Inactive"}
//                 color={activeStates[r.ID] ? "blue" : "red"}
//               />,

//               (() => {
//                 if (r.smartPolicy && r.regularPolicy) {
//                   return (
//                     <div key={`p-${r.ID}`}>
//                       <span className="block text-sm">
//                         Smart = {r.smartPolicy}
//                       </span>
//                       <span className="block text-sm">
//                         Regular = {r.regularPolicy}
//                       </span>
//                     </div>
//                   );
//                 } else if (r.smartPolicy) {
//                   return (
//                     <span key={`p-${r.ID}`}>Smart = {r.smartPolicy}</span>
//                   );
//                 } else if (r.regularPolicy) {
//                   return (
//                     <span key={`p-${r.ID}`}>Regular = {r.regularPolicy}</span>
//                   );
//                 } else {
//                   return <span key={`p-${r.ID}`}>0</span>;
//                 }
//               })(),

//               new Date(r.CreatedOn).toLocaleDateString(),

//               <div className="flex flex-wrap gap-2" key={`act-${r.ID}`}>
//                 <BlueButton onClick={() => handleToggleActiveConfirmation(r)}>
//                   {activeStates[r.ID] ? "Deactivate" : "Activate"}
//                 </BlueButton>

//               </div>,
//             ])}

//             currentPage={page}
//             totalPages={lastPage}
//             onPageChange={setPage}
//             filter={filter}
//           />
//         </>
//       )}

//       {/* Password Modal */}
//       {showPwdModal && selectedemployee && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-lg font-semibold text-[#0A3E91]">
//                 Update Password - {selectedemployee.Shop_Name} (
//                 {selectedemployee.employeeCode})
//               </h3>
//               <button
//                 onClick={() => setShowPwdModal(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 ✕
//               </button>
//             </div>

//             <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
//               <input
//                 value={selectedemployee.ID}
//                 readOnly
//                 className="rounded-lg border px-3 py-2 text-sm bg-gray-100"
//               />
//               <input
//                 value={selectedemployee.Shop_Name}
//                 readOnly
//                 className="rounded-lg border px-3 py-2 text-sm bg-gray-100"
//               />
//               <div className="relative">
//                 <input
//                   type={showPwd ? "text" : "password"}
//                   placeholder="New Password"
//                   className="w-full rounded-lg border px-3 py-2 text-sm"
//                 />
//                 <button
//                   onClick={() => setShowPwd((s) => !s)}
//                   className="absolute right-2 top-2.5 text-slate-500"
//                 >
//                   {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
//                 </button>
//               </div>
//               <BlueButton className="sm:col-span-3">Update Password</BlueButton>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Add employee Modal */}
//       {/* {showAdd && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-lg font-semibold text-[#0A3E91]">
//                 Add employee
//               </h3>
//               <button
//                 onClick={() => setShowAdd(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 ✕
//               </button>
//             </div>

//             <employeeForm
//               onCancel={() => setShowAdd(false)}
//               onSubmit={() => setShowAdd(false)}
//             />
//           </div>
//         </div>
//       )} */}

//       {/* Assign Keys Modal */}
//       {showAssignModal && assignemployee && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-lg font-semibold text-[#0A3E91]">
//                 Assign Keys - {assignemployee.Shop_Name} (
//                 {assignemployee.employeeCode})
//               </h3>
//               <button
//                 onClick={() => setShowAssignModal(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 ✕
//               </button>
//             </div>

//             <Card title="Assign Keys to employee">
//               <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
//                 <input
//                   value={assignemployee.employeeCode || assignemployee.ID}
//                   readOnly
//                   className="rounded-lg border px-3 py-2 text-sm bg-gray-100 w-full"
//                 />
//                 <input
//                   value={assignemployee.Shop_Name}
//                   readOnly
//                   className="rounded-lg border px-3 py-2 text-sm bg-gray-100 w-full"
//                 />
//                 <select className="rounded-lg border px-3 py-2 text-sm w-full">
//                   <option value="regular">Regular</option>
//                   <option value="smart">Smart</option>
//                 </select>
//                 <select className="rounded-lg border px-3 py-2 text-sm w-full">
//                   <option>Regular Key</option>
//                   <option>Regular + Warranty</option>
//                   <option>Smart Key</option>
//                 </select>
//                 <input
//                   placeholder="Start Key (e.g., GE-1000)"
//                   className="rounded-lg border px-3 py-2 text-sm w-full"
//                 />
//                 <input
//                   placeholder="End Key (e.g., GE-1050)"
//                   className="rounded-lg border px-3 py-2 text-sm w-full"
//                 />
//                 <div className="sm:col-span-2 flex justify-end">
//                   <BlueButton>Assign</BlueButton>
//                 </div>
//               </div>
//             </Card>
//           </div>
//         </div>
//       )}
//     </div>
//    </SectionCard>
//    </div>
//   );
// }